import React, { ReactElement, useState } from "react";
import { Waypoint } from "react-waypoint";
import { ThemeProvider } from "styled-components";

import Section from "components/Section";
import { useBreakpoint } from "hooks";
import useIsInEditMode from "hooks/useIsInEditMode";
import { addEditAttributes } from "utils/episerver";

import {
    Heading,
    Content,
    CircularProcess,
    Introduction,
    Step,
    StepsWrapper,
    Steps,
    StepIcon,
    ReadMore,
    IconArrow,
    LinkText,
    StepHeading,
    StepContent,
} from "./Process.styled";
import ProcessProps from "./ProcessProps";

const Process = ({
    identifier,
    theme,
    contentTheme,
    actionTheme,
    heading,
    introduction,
    anchorName,
    steps,
    type = "circular",
    enableNumbers = true,
    enableIcons = true,
}: ProcessProps): ReactElement => {
    const isInEditMode = useIsInEditMode();
    const [activeIds, setActiveIds] = useState<string[]>([]);

    const breakPoint = useBreakpoint();

    return (
        <Section theme={theme} alignItems="center" id={anchorName}>
            <ThemeProvider
                theme={{
                    theme: theme,
                    contentTheme: contentTheme,
                    actionTheme: actionTheme || contentTheme,
                    isInEditMode: isInEditMode,
                }}
            >
                <Content key={identifier || `ProcessContent`}>
                    {(isInEditMode || heading) && (
                        <Heading
                            as="h2"
                            {...addEditAttributes("Heading")}
                            dangerouslySetInnerHTML={{
                                __html: heading || "",
                            }}
                        />
                    )}
                    {(isInEditMode || introduction) && (
                        <Introduction
                            as="div"
                            {...addEditAttributes("Introduction")}
                        >
                            {introduction}
                        </Introduction>
                    )}
                    <StepsWrapper>
                        {type === "circular" && <CircularProcess />}
                        <Steps {...addEditAttributes("ProcessSteps")}>
                            {steps &&
                                steps.map((step, index) => {
                                    const hasIcon = enableIcons && !!step.icon;
                                    const hasReadMore =
                                        step.link &&
                                        step.link.url &&
                                        step.link.linkText;

                                    // Handle the offsets for Waypoint, which defines when it should switch active
                                    let topOffset: string;
                                    let bottomOffset: string;
                                    switch (breakPoint) {
                                        case "XL":
                                            topOffset = "112px";
                                            bottomOffset = hasIcon
                                                ? "70px"
                                                : "18px";
                                            break;
                                        case "L":
                                            topOffset = "98px";
                                            bottomOffset = hasIcon
                                                ? "70px"
                                                : "11px";
                                            break;
                                        case "M":
                                            topOffset = "98px";
                                            bottomOffset = hasIcon
                                                ? "70px"
                                                : "11px";
                                            break;
                                        default:
                                            topOffset = "84px";
                                            bottomOffset = hasIcon
                                                ? "70px"
                                                : "11px";
                                    }

                                    return (
                                        <Waypoint
                                            bottomOffset={bottomOffset}
                                            topOffset={topOffset}
                                            key={`step-${index}`}
                                            onEnter={() => {
                                                setActiveIds(
                                                    step.identifier
                                                        ? [
                                                              ...activeIds,
                                                              step.identifier,
                                                          ]
                                                        : activeIds,
                                                );
                                            }}
                                            onLeave={() => {
                                                setActiveIds(
                                                    activeIds.filter(
                                                        (id) =>
                                                            id !==
                                                            step.identifier,
                                                    ),
                                                );
                                            }}
                                        >
                                            <Step
                                                key={`step-${index}`}
                                                $hasIcon={hasIcon}
                                                $enableNumbers={enableNumbers}
                                                $active={
                                                    // Get the first in array
                                                    activeIds.find(Boolean) ===
                                                    step.identifier
                                                }
                                            >
                                                {enableIcons && step.icon && (
                                                    <StepIcon
                                                        icon={`${step.icon}56`}
                                                        aria-hidden="true"
                                                    />
                                                )}
                                                {step.heading && (
                                                    <StepHeading
                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                                step.heading ||
                                                                "",
                                                        }}
                                                    />
                                                )}
                                                {step.content && (
                                                    <StepContent
                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                                step.content ||
                                                                "",
                                                        }}
                                                    />
                                                )}
                                                {hasReadMore && (
                                                    <ReadMore
                                                        {...step.link}
                                                        hoverSpan={true}
                                                        gtmClickType="quote"
                                                        {...addEditAttributes(
                                                            "Link",
                                                        )}
                                                    >
                                                        <LinkText>
                                                            <IconArrow
                                                                icon={
                                                                    "longArrowRight28"
                                                                }
                                                                aria-hidden="true"
                                                            />
                                                            {step.link
                                                                ?.linkText ||
                                                                ""}
                                                        </LinkText>
                                                    </ReadMore>
                                                )}
                                            </Step>
                                        </Waypoint>
                                    );
                                })}
                        </Steps>
                    </StepsWrapper>
                </Content>
            </ThemeProvider>
        </Section>
    );
};

export default React.memo(Process);
